import { ImageIcon, Loader2, Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { deleteServiceImage, uploadServiceImage } from '@/lib/api/services'
import { serviceImageFallbackStyle } from '@/lib/serviceImage'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {import('@/types/api').Service | null} props.service
 * @param {(service: import('@/types/api').Service) => void} props.onUpdated
 * @param {boolean} [props.disabled]
 */
export function ServiceImageField({ service, onUpdated, disabled = false }) {
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  if (!service?.id) {
    return (
      <div className="rounded-xl border border-dashed border-border/80 bg-muted/20 px-4 py-8 text-center text-sm text-muted-foreground">
        Save the service first, then upload a photo.
      </div>
    )
  }

  async function handleFileChange(event) {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file) return

    setUploading(true)
    setError('')

    try {
      const updated = await uploadServiceImage(service.id, file)
      onUpdated(updated)
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to upload image.')
    } finally {
      setUploading(false)
    }
  }

  async function handleRemove() {
    setUploading(true)
    setError('')

    try {
      const updated = await deleteServiceImage(service.id)
      onUpdated(updated)
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to remove image.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Service photo</p>
          <p className="text-xs text-muted-foreground">
            Shown in Masters and on POS tiles. JPG, PNG, or WebP up to 4 MB.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            Upload
          </Button>
          {service.image_url ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || uploading}
              onClick={handleRemove}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Remove
            </Button>
          ) : null}
        </div>
      </div>

      <div
        className={cn(
          'relative overflow-hidden rounded-2xl border border-border/70 bg-muted/20',
          'aspect-[16/10] max-w-md',
        )}
      >
        {service.image_url ? (
          <img
            src={service.image_url}
            alt={service.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground"
            style={serviceImageFallbackStyle(service)}
          >
            <ImageIcon className="h-8 w-8 opacity-60" />
            <span className="text-xs font-medium">No photo yet</span>
          </div>
        )}
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  )
}
